/**
 * Predictive Dial API client — talks to the backend's predictive dial endpoints.
 *
 * Used by PredictiveDialContext to:
 *   - Start/end sessions
 *   - Decide when to pre-dial
 *   - Pre-dial / bridge / abandon customer legs
 *   - Poll live stats for the agent sidebar
 */
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

export interface PredictiveAgentStats {
  userId: string;
  avgTalkTimeS: number;
  avgDisposeTimeS: number;
  pickupRatePct: number;
  voicemailRatePct: number;
  avgRingTimeS: number;
  sampleSize: number;
  source: 'blended' | 'baseline_only' | 'defaults';
}

export interface PredictiveSessionStart {
  // sessionId can be null when backend reports predictive is globally disabled
  // (PREDICTIVE_DIAL_ENABLED env var unset). In that case all other fields are
  // null/zero and `disabled: true` is set. Frontend treats this as silent
  // fallback to standard PowerDialer.
  sessionId: string | null;
  conferenceName: string | null;
  agentStats: PredictiveAgentStats | null;
  currentPredialPace: number;
  startedAt: string | null;
  disabled?: boolean;
  reason?: string;
}

export interface PredictiveSessionStats {
  sessionId: string;
  conferenceName: string;
  currentPredialPace: number;
  totalDialed: number;
  totalAnswered: number;
  totalPredialAttempts: number;
  totalPredialHits: number;
  totalAbandoned: number;
  abandonmentRate: number;
  agentStats: PredictiveAgentStats;
  startedAt: string;
  endedAt: string | null;
}

export interface ShouldPredialDecision {
  should: boolean;
  reason: string;
  nextCheckMs?: number;
}

export interface PredialResult {
  statusCode: number;
  customerCallSid?: string;
  holdingConference?: string;
  skipped?: boolean;
  reason?: string;
}

export interface BridgeResult {
  statusCode: number;
  bridged: boolean;
}

export interface AbandonResult {
  statusCode: number;
  abandoned: boolean;
  newPace: number;
  throttled: boolean;
  killed: boolean;
}

const predictiveDialService = {
  /**
   * Start a new predictive session. Reserves a persistent conference name the
   * agent will join for the entire session.
   */
  async startSession(source: 'dialiq' | 'manual_power' = 'dialiq'): Promise<PredictiveSessionStart> {
    const res = await api.post<PredictiveSessionStart>({
      url: getApiUrl('/communication/predictive/sessions/start'),
      body: { source },
      auth: true,
    });
    return res.data;
  },

  /**
   * End the active session.
   */
  async endSession(
    sessionId: string,
    reason: 'agent_stopped' | 'abandonment_kill' | 'completed' | 'error' = 'agent_stopped',
  ): Promise<{ statusCode: number; sessionId: string; reason: string }> {
    const res = await api.post<{ statusCode: number; sessionId: string; reason: string }>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/end`),
      body: { reason },
      auth: true,
    });
    return res.data;
  },

  /**
   * Poll for live session stats. UI sidebar calls this every 2-5 seconds.
   */
  async getStats(sessionId: string): Promise<PredictiveSessionStats> {
    const res = await api.get<PredictiveSessionStats>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/stats`),
      auth: true,
    });
    return res.data;
  },

  /**
   * Ask the backend whether NOW is the right time to pre-dial.
   * Returns nextCheckMs so we can schedule the next poll efficiently.
   */
  async shouldPredial(
    sessionId: string,
    currentCallElapsedMs: number,
  ): Promise<ShouldPredialDecision> {
    const res = await api.post<ShouldPredialDecision>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/should-predial`),
      body: { currentCallElapsedMs },
      auth: true,
    });
    return res.data;
  },

  /**
   * Pre-dial the next lead. Customer dials into a holding conference + hears
   * "please hold" TTS. Returns the customer CallSid we'll later /bridge.
   */
  async predial(
    sessionId: string,
    params: { leadId: string; toNumber: string; fromNumber: string },
  ): Promise<PredialResult> {
    const res = await api.post<PredialResult>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/predial`),
      body: params,
      auth: true,
    });
    return res.data;
  },

  /**
   * Bridge the pre-dialed customer into the agent's persistent conference.
   * Called when the agent finishes their current call.
   */
  async bridge(
    sessionId: string,
    params: { customerCallSid: string; holdingConference: string; agentParentCallSid?: string },
  ): Promise<BridgeResult> {
    const res = await api.post<BridgeResult>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/bridge`),
      body: params,
      auth: true,
    });
    return res.data;
  },

  /**
   * Abandon a pre-dialed customer (agent couldn't make it in time).
   * Plays the TCPA-compliant "we'll call you back" + hangup TwiML.
   * Returns updated pace info — frontend may need to throttle going forward.
   */
  async abandon(
    sessionId: string,
    params: { customerCallSid: string; leadId: string },
  ): Promise<AbandonResult> {
    const res = await api.post<AbandonResult>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/abandon`),
      body: params,
      auth: true,
    });
    return res.data;
  },

  /**
   * Dial a lead directly into the agent's persistent conference. Used when
   * the agent is idle (no current call) — the customer joins the conference
   * the agent is already in, no pre-dial-hold detour needed.
   */
  async dialLead(
    sessionId: string,
    params: { leadId: string; toNumber: string; fromNumber: string; agentParentCallSid?: string },
  ): Promise<{ statusCode: number; customerCallSid: string; conferenceName: string }> {
    const res = await api.post<{ statusCode: number; customerCallSid: string; conferenceName: string }>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/dial-lead`),
      body: params,
      auth: true,
    });
    return res.data;
  },

  /**
   * Hang up a single customer's leg without touching the agent's leg in the
   * persistent conference. Used by handleEndCall in predictive mode so the
   * agent stays in the conference for the next bridge.
   */
  async endCustomerLeg(
    sessionId: string,
    params: { customerCallSid: string },
  ): Promise<{ statusCode: number; ended: boolean; reason?: string }> {
    const res = await api.post<{ statusCode: number; ended: boolean; reason?: string }>({
      url: getApiUrl(`/communication/predictive/sessions/${sessionId}/end-customer-leg`),
      body: params,
      auth: true,
    });
    return res.data;
  },
};

export default predictiveDialService;
